(() => {
  document.documentElement.classList.add('kosbling-enhanced');

  const aboutSection = document.querySelector('.kosbling-about');
  if (aboutSection) aboutSection.id = 'about';

  const navigationControllers = new WeakMap();
  const openNavigationControllers = new Set();

  class KosblingNavigationController {
    constructor(root, dependencies = {}) {
      this.root = root;
      this.document = dependencies.document || document;
      this.window = dependencies.window || window;
      this.button = root.querySelector('[data-kosbling-menu-toggle]');
      this.drawer = root.querySelector('[data-kosbling-mobile-menu]');
      this.backdrop = root.querySelector('[data-kosbling-drawer-backdrop]');
      this.closeButton = root.querySelector('[data-kosbling-drawer-close]');
      this.disclosureButtons = [...root.querySelectorAll('[data-kosbling-submenu-toggle]')];
      this.opener = null;
      this.isOpen = false;
      this.previousHtmlOverflow = '';
      this.previousBodyOverflow = '';
      this.handleToggle = () => this.setOpen(!this.isOpen);
      this.handleClose = () => this.setOpen(false);
      this.handleBackdropClose = () => this.setOpen(false);
      this.handleDocumentKeydown = (event) => this.onDocumentKeydown(event);
      this.handleDocumentFocusin = (event) => {
        if (!this.isOpen || this.drawer.contains(event.target)) return;
        (this.focusableElements()[0] || this.closeButton).focus();
      };
      this.handleDrawerClick = (event) => {
        const link = event.target.closest?.('a[href]');
        if (link && this.root.contains(link)) this.setOpen(false);
      };
      this.handleResize = () => {
        if (this.window.matchMedia('(min-width: 990px)').matches) this.setOpen(false, { returnFocus: false });
      };
      this.handleDisclosure = (event) => this.toggleDisclosure(event.currentTarget || event.target);
      this.valid = Boolean(this.button && this.drawer && this.backdrop && this.closeButton);
      if (!this.valid) return;

      this.button.setAttribute('aria-expanded', 'false');
      this.drawer.hidden = true;
      this.button.addEventListener('click', this.handleToggle);
      this.backdrop.addEventListener('click', this.handleBackdropClose);
      this.closeButton.addEventListener('click', this.handleClose);
      this.drawer.addEventListener('click', this.handleDrawerClick);
      this.document.addEventListener('keydown', this.handleDocumentKeydown);
      this.document.addEventListener('focusin', this.handleDocumentFocusin);
      this.window.addEventListener('resize', this.handleResize);
      this.disclosureButtons.forEach((button) => button.addEventListener('click', this.handleDisclosure));
    }

    focusableElements() {
      return [...this.drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getAttribute?.('aria-hidden') !== 'true' && !element.closest?.('[hidden]'));
    }

    setOpen(open, { returnFocus = true } = {}) {
      if (!this.valid || this.isOpen === open) return;
      if (open) {
        for (const controller of [...openNavigationControllers]) controller.setOpen(false, { returnFocus: false });
        this.opener = this.button;
        this.previousHtmlOverflow = this.document.documentElement.style.overflow;
        this.previousBodyOverflow = this.document.body.style.overflow;
        this.document.documentElement.style.overflow = 'hidden';
        this.document.body.style.overflow = 'hidden';
        this.isOpen = true;
        openNavigationControllers.add(this);
        this.button.setAttribute('aria-expanded', String(open));
        this.drawer.hidden = !open;
        this.document.documentElement.classList.add('kosbling-menu-open');
        (this.focusableElements()[0] || this.drawer).focus();
        return;
      }

      this.isOpen = false;
      openNavigationControllers.delete(this);
      this.button.setAttribute('aria-expanded', String(open));
      this.drawer.hidden = !open;
      this.document.documentElement.style.overflow = this.previousHtmlOverflow;
      this.document.body.style.overflow = this.previousBodyOverflow;
      if (openNavigationControllers.size === 0) this.document.documentElement.classList.remove('kosbling-menu-open');
      if (returnFocus) this.opener?.focus();
      this.opener = null;
    }

    toggleDisclosure(button) {
      if (!button || !this.root.contains(button)) return;
      const panelId = button.getAttribute('aria-controls');
      const panel = panelId ? this.root.querySelector(`#${panelId}`) : null;
      if (!panel) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.setOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = this.focusableElements();
      if (!controls.length) {
        event.preventDefault();
        this.drawer.focus();
        return;
      }
      const first = controls[0];
      const last = controls.at(-1);
      if (!this.drawer.contains(this.document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && this.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && this.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    dispose() {
      if (!this.valid) return;
      this.setOpen(false, { returnFocus: false });
      this.button.removeEventListener('click', this.handleToggle);
      this.backdrop.removeEventListener('click', this.handleBackdropClose);
      this.closeButton.removeEventListener('click', this.handleClose);
      this.drawer.removeEventListener('click', this.handleDrawerClick);
      this.document.removeEventListener('keydown', this.handleDocumentKeydown);
      this.document.removeEventListener('focusin', this.handleDocumentFocusin);
      this.window.removeEventListener('resize', this.handleResize);
      this.disclosureButtons.forEach((button) => button.removeEventListener('click', this.handleDisclosure));
    }
  }

  function initializeKosblingNavigation(root) {
    if (!root || navigationControllers.has(root)) return navigationControllers.get(root) || null;
    const controller = new KosblingNavigationController(root);
    if (!controller.valid) return null;
    navigationControllers.set(root, controller);
    return controller;
  }

  function initializeKosblingNavigationWithin(target = document) {
    if (target.matches?.('[data-kosbling-navigation]')) initializeKosblingNavigation(target);
    target.querySelectorAll?.('[data-kosbling-navigation]').forEach((root) => initializeKosblingNavigation(root));
  }

  function disposeKosblingNavigationWithin(target) {
    const roots = [];
    if (target?.matches?.('[data-kosbling-navigation]')) roots.push(target);
    target?.querySelectorAll?.('[data-kosbling-navigation]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      navigationControllers.get(root)?.dispose();
      navigationControllers.delete(root);
    });
  }

  window.KosblingNavigation = Object.freeze({
    KosblingNavigationController,
    initialize: initializeKosblingNavigation,
    dispose: disposeKosblingNavigationWithin
  });

  initializeKosblingNavigationWithin();
  document.addEventListener('shopify:section:load', (event) => initializeKosblingNavigationWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => disposeKosblingNavigationWithin(event.target));

  function initKosblingContactDialog() {
    const autoOpenStorageKey = 'kosbling.contact.lastShown.v1';
    const autoOpenInterval = 14 * 24 * 60 * 60 * 1000;
    const dialog = document.querySelector('[data-kosbling-contact-dialog]');
    if (!dialog) return;
    const panel = dialog.querySelector('.kosbling-contact-dialog__panel');
    const openers = document.querySelectorAll('[data-kosbling-contact-open]');
    const emailPath = dialog.querySelector('[data-kosbling-contact-email]');
    const emailField = dialog.querySelector('#KosblingContactEmail');
    let opener = null;

    const focusable = () => [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    const close = () => {
      if (dialog.hidden) return;
      dialog.hidden = true;
      document.documentElement.classList.remove('kosbling-contact-open');
      opener?.focus();
      opener = null;
    };
    const open = (trigger) => {
      opener = trigger || document.activeElement;
      dialog.hidden = false;
      document.documentElement.classList.add('kosbling-contact-open');
      const result = dialog.querySelector('[data-kosbling-contact-result]');
      (result || dialog.querySelector('#KosblingContactEmail') || panel)?.focus();
    };
    const rememberAutoOpen = (now) => {
      try {
        window.localStorage.setItem(autoOpenStorageKey, String(now));
      } catch {
        // Contact remains available when browser storage is disabled.
      }
    };
    const shouldAutoOpen = (now) => {
      if (window.Shopify?.designMode) return false;
      try {
        const stored = window.localStorage.getItem(autoOpenStorageKey);
        const lastShown = stored === null ? Number.NaN : Number.parseInt(stored, 10);
        return !Number.isFinite(lastShown) || lastShown > now || now - lastShown >= autoOpenInterval;
      } catch {
        return true;
      }
    };

    openers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger)));
    emailPath?.addEventListener('click', () => emailField?.focus());
    dialog.querySelectorAll('[data-kosbling-contact-close]').forEach((trigger) => trigger.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (dialog.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
      if (event.key !== 'Tab') return;
      const controls = focusable();
      if (!controls.length) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    const now = Date.now();
    if (dialog.querySelector('[data-kosbling-contact-auto-open]')) {
      rememberAutoOpen(now);
      open(null);
    } else if (shouldAutoOpen(now)) {
      rememberAutoOpen(now);
      open(null);
    }
  }

  initKosblingContactDialog();

  const campaignAnchors = new Set(['community', 'about']);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;

    const link = event.target.closest?.('a[data-kosbling-anchor][href^="#"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;

    let anchorId;
    try {
      anchorId = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }
    if (!campaignAnchors.has(anchorId)) return;

    const target = document.getElementById(anchorId);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    window.history.pushState(null, '', hash);
  });

  function getCompatiblePairs(styleKey, compatibility) {
    if (!styleKey || !compatibility || !Array.isArray(compatibility[styleKey])) return [];
    return compatibility[styleKey].map((value) => String(value).trim()).filter(Boolean);
  }

  function findSellableVariant(variants, selectedOptions) {
    if (!Array.isArray(variants) || !Array.isArray(selectedOptions) || selectedOptions.some((value) => !value)) return null;
    return variants.find((variant) => variant.available && variant.options.length === selectedOptions.length && variant.options.every((value, index) => value === selectedOptions[index])) || null;
  }

  function getKosblingSelectedOptions(state) {
    return state.optionNames.map((name) => {
      if (name === 'Carrier + Technique') return state.pair;
      if (name === 'Size or Quantity') return state.size;
      return '';
    });
  }

  function validateKosblingCustom(state) {
    const compatiblePairs = getCompatiblePairs(state.style, state.compatibility);
    if (state.style && state.pair && !compatiblePairs.includes(state.pair)) {
      return { valid: false, variant: null, matchedVariant: null, message: 'That Carrier + Technique is not available for this style.' };
    }
    if (!Array.isArray(state.variants) || state.variants.length === 0) {
      return { valid: false, variant: null, matchedVariant: null, message: 'Select a Shopify product in the theme editor.' };
    }
    if (!state.optionNames.includes('Carrier + Technique')) {
      return { valid: false, variant: null, matchedVariant: null, message: 'The selected product needs a Carrier + Technique option.' };
    }
    const unsupportedOptions = state.optionNames.filter((name) => !['Carrier + Technique', 'Size or Quantity'].includes(name));
    if (unsupportedOptions.length > 0) {
      return { valid: false, variant: null, matchedVariant: null, message: `The selected product has unsupported variant options: ${unsupportedOptions.join(', ')}.` };
    }

    const required = [
      ['style', 'Style'],
      ['pair', 'Carrier + Technique'],
      ['collectible', 'Collectible / IP'],
      ['color', 'Color Direction'],
      ['size', 'Size or Quantity']
    ];
    const missing = required.find(([key]) => !state[key]);
    if (missing) {
      return { valid: false, variant: null, matchedVariant: null, message: `Choose ${missing[1]} to continue.` };
    }
    if (compatiblePairs.length === 0) {
      return { valid: false, variant: null, matchedVariant: null, message: 'No Carrier + Technique is configured for this style.' };
    }

    const selectedOptions = getKosblingSelectedOptions(state);
    const matchedVariant = state.variants.find((variant) => variant.options.length === selectedOptions.length && variant.options.every((value, index) => value === selectedOptions[index])) || null;
    const variant = findSellableVariant(state.variants, selectedOptions);
    const sizeIsProperty = !state.optionNames.includes('Size or Quantity');

    if (!matchedVariant) {
      return { valid: false, variant: null, matchedVariant: null, selectedOptions, sizeIsProperty, message: 'No Shopify variant matches this configuration.' };
    }
    if (!variant) {
      return { valid: false, variant: null, matchedVariant, selectedOptions, sizeIsProperty, message: 'This Shopify variant is unavailable.' };
    }
    return { valid: true, variant, matchedVariant: variant, selectedOptions, sizeIsProperty, message: 'This configuration is available.' };
  }

  function formatKosblingVariantPrice(price, currency) {
    const cents = Number(price);
    if (!Number.isFinite(cents) || !currency) return 'Unavailable';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return String(price);
    }
  }

  function renderKosblingCustomSummary(root, state) {
    const summary = root.querySelector('[data-kosbling-summary]');
    if (!summary) return;
    const validation = validateKosblingCustom(state);
    const variant = validation.matchedVariant;
    const lines = [
      `Style: ${state.style || 'Not chosen'}`,
      `Carrier + Technique: ${state.pair || 'Not chosen'}`,
      `Collectible / IP: ${state.collectible || 'Not chosen'}`,
      `Color Direction: ${state.color || 'Not chosen'}`,
      `Size or Quantity: ${state.size || 'Not chosen'}`,
      `Variant price: ${variant ? formatKosblingVariantPrice(variant.price, root.dataset.currency) : 'Not resolved'}`,
      `Variant SKU: ${variant?.sku || 'Not resolved'}`,
      `Availability: ${variant ? (variant.available ? 'Available' : 'Unavailable') : 'Not resolved'}`
    ];
    if (variant && Number.isFinite(Number(variant.inventory_quantity))) lines.push(`Inventory: ${variant.inventory_quantity}`);
    summary.textContent = lines.join('\n');
  }

  function parseKosblingJson(root, selector, fallback) {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return fallback;
    }
  }

  function hasKosblingSellableVariant(state, key, value) {
    const candidate = { ...state, [key]: value };
    return state.variants.some((variant) => {
      if (!variant.available || variant.options.length !== state.optionNames.length) return false;
      return state.optionNames.every((name, index) => {
        if (!['Carrier + Technique', 'Size or Quantity'].includes(name)) return false;
        const selected = name === 'Carrier + Technique' ? candidate.pair : candidate.size;
        return selected ? variant.options[index] === selected : true;
      });
    });
  }

  function syncKosblingCustom(root, state) {
    const compatiblePairs = getCompatiblePairs(state.style, state.compatibility);
    root.querySelectorAll('[data-kosbling-choice="pair"]').forEach((choice) => {
      choice.disabled = !state.style || !compatiblePairs.includes(choice.value) || !hasKosblingSellableVariant({ ...state, size: '' }, 'pair', choice.value);
    });
    root.querySelectorAll('[data-kosbling-choice="size"]').forEach((choice) => {
      choice.disabled = state.optionNames.includes('Size or Quantity') && (!state.pair || !hasKosblingSellableVariant(state, 'size', choice.value));
    });

    const validation = validateKosblingCustom(state);
    const variantId = root.querySelector('[data-kosbling-variant-id]');
    if (variantId) variantId.value = validation.variant ? String(validation.variant.id) : '';
    root.querySelectorAll('[data-kosbling-property]').forEach((propertyInput) => {
      const key = propertyInput.dataset.kosblingProperty;
      propertyInput.value = validation.valid ? (state[key] || '') : '';
      if (key === 'size' && !validation.sizeIsProperty) propertyInput.value = '';
    });

    const submit = root.querySelector('[data-kosbling-submit]');
    if (submit) submit.disabled = !validation.valid;
    const message = root.querySelector('[data-kosbling-message]');
    if (message) {
      message.hidden = validation.valid;
      message.textContent = validation.message;
    }
    const media = root.querySelector('[data-kosbling-custom-media]');
    if (media) {
      media.src = state.mediaSrc || media.dataset.fallbackSrc;
      media.alt = state.mediaAlt || media.dataset.fallbackAlt;
    }
    renderKosblingCustomSummary(root, state);
    return validation;
  }

  function initKosblingCustomStudio(root) {
    if (!root || root.dataset.kosblingInitialized === 'true') return root?.kosblingCustomState || null;
    root.dataset.kosblingInitialized = 'true';
    const state = {
      style: '',
      pair: '',
      collectible: '',
      color: '',
      size: '',
      mediaSrc: '',
      mediaAlt: '',
      variants: parseKosblingJson(root, '[data-kosbling-variants]', []),
      optionNames: parseKosblingJson(root, '[data-kosbling-option-names]', []),
      compatibility: parseKosblingJson(root, '[data-kosbling-compatibility]', {})
    };
    root.kosblingCustomState = state;

    root.addEventListener('change', (event) => {
      const choice = event.target;
      const key = choice?.dataset?.kosblingChoice;
      if (!key || choice.disabled) return;
      root.querySelectorAll(`[data-kosbling-choice="${key}"]`).forEach((groupChoice) => {
        if (groupChoice !== choice) groupChoice.checked = false;
      });
      if (key === 'style') {
        state.style = choice.value;
        state.mediaSrc = choice.dataset.mediaSrc || '';
        state.mediaAlt = choice.dataset.mediaAlt || '';
        state.pair = '';
        state.size = '';
        root.querySelectorAll('[data-kosbling-choice="pair"]').forEach((pairChoice) => { pairChoice.checked = false; });
        root.querySelectorAll('[data-kosbling-choice="size"]').forEach((sizeChoice) => { sizeChoice.checked = false; });
      } else if (key === 'pair') {
        if (!getCompatiblePairs(state.style, state.compatibility).includes(choice.value)) return;
        state.pair = choice.value;
        if (state.optionNames.includes('Size or Quantity') && state.size && !hasKosblingSellableVariant(state, 'size', state.size)) {
          state.size = '';
          root.querySelectorAll('[data-kosbling-choice="size"]').forEach((sizeChoice) => { sizeChoice.checked = false; });
        }
      } else if (['collectible', 'color', 'size'].includes(key)) {
        state[key] = choice.value;
      }
      syncKosblingCustom(root, state);
    });

    syncKosblingCustom(root, state);
    return state;
  }

  window.KosblingCustomStudio = Object.freeze({
    getCompatiblePairs,
    findSellableVariant,
    validateKosblingCustom,
    renderKosblingCustomSummary,
    initKosblingCustomStudio
  });

  document.querySelectorAll('[data-kosbling-custom-studio]').forEach((root) => initKosblingCustomStudio(root));
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target?.matches?.('[data-kosbling-custom-studio]')) initKosblingCustomStudio(target);
    target?.querySelectorAll?.('[data-kosbling-custom-studio]').forEach((root) => initKosblingCustomStudio(root));
  });
})();
